import React, { useState, useEffect } from "react";
import { getOrdersReport } from "../service/dashboardService";
import MonthlyDataGraph from "./MonthlyDataGraph";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const Dashboard = () => {
  const lsUserVO = process.env.REACT_APP_LS_USER_VO;
  const pimsUserVO = JSON.parse(localStorage.getItem(lsUserVO));

  const [ordersReport, setOrdersReport] = useState(null);
  const [error, setError] = useState(false); // Track errors
  const [showSales, setShowSales] = useState(true);
  const [showPurchase, setShowPurchase] = useState(false);

  const fetchOrdersReport = async () => {
    try {
      const reportData = await getOrdersReport(pimsUserVO.branchId);
      setOrdersReport(reportData);
    } catch (error) {
      console.error("Error fetching sales orders:", error);
      setError(true); // Set error state when the API fails
    }
  };

  useEffect(() => {
    fetchOrdersReport();
  }, []);

  if (error) {
    return <div className="text-center text-red-600">Failed to load data. Please try again later.</div>;
  }

  if (!ordersReport) {
    return <div>Loading...</div>;
  }

   const handleSalesToggle = (e) => {
    setShowSales(e.target.checked);
    if (e.target.checked) {
      setShowPurchase(false);
    }
  };

  const handlePurchaseToggle = (e) => {
    setShowPurchase(e.target.checked);
    if (e.target.checked) {
      setShowSales(false);
    }
  };

  // Continue with the rest of your component as usual
  const { salesReport, purchaseReport } = ordersReport;
  const comparisonData = salesReport
    ? [
        {
          name: "Amount",
          lastMonth: salesReport.lastMonth.price,
          currentMonth: salesReport.currentMonth.price,
        },
        {
          name: "Items",
          lastMonth: salesReport.lastMonth.items,
          currentMonth: salesReport.currentMonth.items,
        },
        {
          name: "Qty",
          lastMonth: salesReport.lastMonth.qty,
          currentMonth: salesReport.currentMonth.qty,
        },
      ]
    : [];

 const ComparisonChart = ({ title }) => (
  <div className="mt-6 p-4 rounded-lg bg-white shadow-md">
    <h3 className="text-2xl font-bold mb-4 text-gray-700">{title}</h3>
    {comparisonData.length > 0 ? (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={comparisonData}>
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="lastMonth" fill="#ff7300" name="Last Month" />
          <Bar dataKey="currentMonth" fill="#8884d8" name="Current Month" />
        </BarChart>
      </ResponsiveContainer>
    ) : (
      <div className="text-center text-gray-500">No data available</div>
    )}
  </div>
);

  return (
    <div className="min-h-screen flex flex-col p-4 bg-gradient-to-r from-green-200 via-green-100 to-green-50">
      <h1 className="text-4xl md:text-2xl font-extrabold mb-8 text-green-900 drop-shadow-lg text-center">
        Dashboard
      </h1>

      {/* Toggle Checkboxes */}
      <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4 mb-3">
        <label htmlFor="salesToggle" className="flex items-center">
          <input
            id="salesToggle"
            type="checkbox"
            className="form-checkbox text-green-500"
            checked={showSales}
            onChange={handleSalesToggle}
          />
          <span className="ml-2 text-lg font-semibold text-gray-700">
            Sales Report
          </span>
        </label>
        <label htmlFor="purchaseToggle" className="flex items-center">
          <input
            id="purchaseToggle"
            type="checkbox"
            className="form-checkbox text-green-500"
            checked={showPurchase}
            onChange={handlePurchaseToggle}
          />
          <span className="ml-2 text-lg font-semibold text-gray-700">
            Purchase Report
          </span>
        </label>
      </div>

      {/* Responsive Layout for Sales and Purchase Reports */}
      <div className="flex flex-col lg:flex-row gap-8 flex-grow">
        {/* Sales Report Section */}
        {showSales && salesReport && (
          <div className="bg-green-50 shadow-2xl rounded-lg p-6 border-l-8 border-green-500 flex-grow">
            <h2 className="text-3xl md:text-2xl font-bold mb-4 text-black-700 drop-shadow">
              Sales Report
            </h2>
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="bg-gradient-to-r from-yellow-300 to-yellow-200 p-5 rounded-lg shadow-lg hover:scale-105 transition duration-500 flex-grow">
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  Sales Summary
                </h3>
                <div className="flex items-center">
                  <div className="mr-4">
                    <span className="font-bold text-gray-700">
                      Total Amount:
                    </span>
                    <span className="text-base md:text-lg font-semibold text-red-800">
                      {" "}
                      {new Intl.NumberFormat("en-IN", {
                        style: "currency",
                        currency: "INR",
                      }).format(salesReport.total.price)}
                    </span>
                  </div>
                  <div className="mr-4">
                    <span className="font-bold text-gray-700">
                      Total Items:
                    </span>
                    <span className="text-base md:text-lg font-semibold text-red-800">
                      {" "}
                      {new Intl.NumberFormat("en-IN").format(
                        salesReport.total.items
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="font-bold text-gray-700">Total Qty:</span>
                    <span className="text-base md:text-lg font-semibold text-red-800">
                      {" "}
                      {new Intl.NumberFormat("en-IN").format(
                        salesReport.total.qty
                      )}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-green-300 to-green-200 p-5 rounded-lg shadow-lg hover:scale-105 transition duration-500 flex-grow">
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  Sales Target
                </h3>
                <div className="flex items-center">
                  <div className="mr-4">
                    <span className="font-bold text-gray-700">
                      Sales Amount:
                    </span>
                    <span className="text-base md:text-lg font-semibold text-pink-800">
                      {" "}
                      {new Intl.NumberFormat("en-IN", {
                        style: "currency",
                        currency: "INR",
                      }).format(salesReport.maximum.price)}
                    </span>
                  </div>
                  <div className="mr-4">
                    <span className="font-bold text-gray-700">
                      Total Items:
                    </span>
                    <span className="text-base md:text-lg font-semibold text-pink-800">
                      {" "}
                      {new Intl.NumberFormat("en-IN").format(
                        salesReport.maximum.items
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="font-bold text-gray-700">Total Qty:</span>
                    <span className="text-base md:text-lg font-semibold text-pink-800">
                      {" "}
                      {new Intl.NumberFormat("en-IN").format(
                        salesReport.maximum.qty
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* Comparison Chart */}
            <ComparisonChart title="Sales Comparison" />

            {/* Monthly Sales Graph */}
            <div className="w-full mt-6">
              <MonthlyDataGraph
                monthYearData={salesReport.monthYear}
                title="Monthly Sales Data"
              />
            </div>
          </div>
        )}

        {/* Purchase Report Section */}
        {showPurchase && purchaseReport && (
          <div className="bg-green-50 shadow-2xl rounded-lg p-6 border-l-8 border-green-500 flex-grow">
            <h2 className="text-3xl md:text-2xl font-bold mb-4 text-black-700 drop-shadow">
              Purchase Report
            </h2>
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="bg-gradient-to-r from-yellow-300 to-yellow-200 p-5 rounded-lg shadow-lg hover:scale-105 transition duration-500 flex-grow">
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  Purchase Summary
                </h3>
                <div className="flex items-center">
                  <div className="mr-4">
                    <span className="font-bold text-gray-700">
                      Total Amount:
                    </span>
                    <span className="text-base md:text-lg font-semibold text-red-800">
                      {" "}
                      {new Intl.NumberFormat("en-IN", {
                        style: "currency",
                        currency: "INR",
                      }).format(purchaseReport.total.price)}
                    </span>
                  </div>
                  <div className="mr-4">
                    <span className="font-bold text-gray-700">
                      Total Items:
                    </span>
                    <span className="text-base md:text-lg font-semibold text-red-800">
                      {" "}
                      {new Intl.NumberFormat("en-IN").format(
                        purchaseReport.total.items
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="font-bold text-gray-700">Total Qty:</span>
                    <span className="text-base md:text-lg font-semibold text-red-800">
                      {" "}
                      {new Intl.NumberFormat("en-IN").format(
                        purchaseReport.total.qty
                      )}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-green-300 to-green-200 p-5 rounded-lg shadow-lg hover:scale-105 transition duration-500 flex-grow">
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  Purchase Target
                </h3>
                <div className="flex items-center">
                  <div className="mr-4">
                    <span className="font-bold text-gray-700">
                      Purchase Amount:
                    </span>
                    <span className="text-base md:text-lg font-semibold text-pink-800">
                      {" "}
                      {new Intl.NumberFormat("en-IN", {
                        style: "currency",
                        currency: "INR",
                      }).format(purchaseReport.maximum.price)}
                    </span>
                  </div>
                  <div className="mr-4">
                    <span className="font-bold text-gray-700">
                      Total Items:
                    </span>
                    <span className="text-base md:text-lg font-semibold text-pink-800">
                      {" "}
                      {new Intl.NumberFormat("en-IN").format(
                        purchaseReport.maximum.items
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="font-bold text-gray-700">Total Qty:</span>
                    <span className="text-base md:text-lg font-semibold text-pink-800">
                      {" "}
                      {new Intl.NumberFormat("en-IN").format(
                        purchaseReport.maximum.qty
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* Comparison Chart */}
            <ComparisonChart title="Purchase Comparison" />

            {/* Monthly Purchase Graph */}
            <div className="w-full mt-6">
              <MonthlyDataGraph
                monthYearData={purchaseReport.monthYear}
                title="Monthly Purchase Data"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
